/**
 * Adaptive procedural music (plan 08 A3): a lookahead step sequencer — military percussion +
 * detuned-saw drone + sparse ostinato, 0 asset bytes. 4/4, 16 steps per bar; tempo comes from the
 * level's MusicTheme (plan 17 D3: THEME_L1 = 100/112 BPM A minor, THEME_L2 = 92/104 BPM E minor;
 * plan 19 D5 adds THEME_L3 = 88/100 BPM D minor for Operation Duskline).
 * A 25 ms interval books each whole bar once its start is ≤ 100 ms ahead on
 * ctx.currentTime, so frame hitches never jitter the grid. Intensity layers (march/engage/boss from cues.intensity) switch ONLY on the
 * next bar line with a 1-bar gain crossfade; won/lost fire a stinger and go silent.
 */
import { tone, VoicePool } from './synth';
import { motifNotes } from './motif';
import { makeRng, runSeed, type Rng } from './rng';
import type { Intensity } from './cues';

export const STEPS_PER_BAR = 16;
/** Scheduler wake-up (ms) and booking horizon (s). 4× wake-ups per horizon → no gaps. */
export const TICK_MS = 25;
export const LOOKAHEAD_S = 0.1;

/** Replace an existing four-note ostinato occasionally, leaving set pieces intact. */
export function combatMotifForBar(theme: MusicTheme, layer: 'march' | 'engage' | 'boss', bar: number, spec: BarSpec): ReturnType<typeof motifNotes> {
  if (bar % 4 !== 3 || spec.fill || spec.entryFill || spec.drop || spec.pattern.ostinato.length < 4) return [];
  const root = theme.droneHz * 4 * (layer === 'boss' ? FOURTH : 1);
  const scale = stepDuration(layer === 'boss' ? theme.bossBpm : theme.bpm) / 0.15;
  return motifNotes(root, 'combat').map(note => ({ ...note, offset: note.offset * scale, duration: note.duration * scale }));
}

/** Seconds per step at a tempo (16th notes in 4/4). */
export function stepDuration(bpm: number): number {
  return 60 / bpm / 4;
}

/** Seconds per bar at a tempo. */
export function barDuration(bpm: number): number {
  return stepDuration(bpm) * STEPS_PER_BAR;
}

/**
 * PURE: step indices k ≥ 0 whose scheduled time startTime + k·stepDur falls in [fromTime, toTime).
 * The scheduler's window contract — used to book exactly the steps of one bar, no off-by-ones at
 * the edges. Empty when the window is empty or entirely before startTime.
 */
export function stepsInWindow(fromTime: number, toTime: number, startTime: number, stepDur: number): number[] {
  const steps: number[] = [];
  if (!(stepDur > 0) || !(toTime > fromTime)) return steps;
  const first = Math.max(0, Math.ceil((fromTime - startTime) / stepDur - 1e-9));
  const last = Math.ceil((toTime - startTime) / stepDur - 1e-9) - 1;
  for (let k = first; k <= last; k++) steps.push(k);
  return steps;
}

/**
 * Pattern bank: one 16-step boolean row per layer/per element. Layers are cumulative on the bar
 * (march ⊂ engage ⊂ boss by design — the crossfade is a gain ramp, not a rewrite).
 *  k = kick, s = snare, S = accent snare, t = tom, o = ostinato pluck.
 *  Plan 24 adds the optional `grace` row: 32nd-note grace hits (Field-Line rudiments) at
 *  half-step offsets — a flam = one grace 1/32 before an accent; a drag = two graces before
 *  a snare; rolls = alternating doubles. The shipped THEME banks carry none (their pins stay
 *  verbatim); the per-level FORM tables (below) author them.
 */
export interface Pattern {
  kick: number[];
  snare: number[];
  accent: number[];
  tom: number[];
  ostinato: number[];
  /** Grace hits at fractional (half-step = 32nd) positions, lower velocity. */
  grace?: Array<{ at: number; element: 'snare' | 'tom' }>;
}

/** Song-form section id (plan 24 Goal (a)): A → A′ → B as the march counter climbs. */
export type SectionKey = 'A' | "A'" | 'B';

/** PURE: which section a march/boss counter is in — counters 1–4 A, 5–8 A′, 9+ B. */
export function sectionFor(counter: number): SectionKey {
  if (counter <= 4) return 'A';
  if (counter <= 8) return "A'";
  return 'B';
}

/** Per-level song form (plan 24 Goal (a)/(b)): section pattern variants per intensity state
 *  (omitted rows fall back to the theme's bank), the 8-bar-boundary fill (march), the boss
 *  entry figure, and the one drone-only silence drop in B. SEPARATE from MusicTheme so the
 *  THEME_L1 verbatim pin keeps meaning "all pre-existing fields verbatim". */
export interface FormSpec {
  sections: Partial<Record<SectionKey, Partial<Record<'march' | 'engage' | 'boss', Pattern>>>>;
  /** Booked as a whole bar at counter ≡ 0 (mod 8), march only, counter > 0. */
  fill: Pattern;
  /** The boss-entry bar's figure (supersedes any form fill on that bar). */
  entry: Pattern;
  /** The counter of the march silence drop (drone-only bar inside B). */
  dropCounter: number;
}

/** One bar's fully-computed booking spec — the pure output of `barSpecFor`. */
export interface BarSpec {
  section: SectionKey | 'fill' | 'drop' | 'boss-entry';
  pattern: Pattern;
  mix: { perc: number; drone: number; ostinato: number };
  fill: boolean;
  entryFill: boolean;
  drop: boolean;
  brightTom: boolean;
  patternHash: string;
}

/** Rotate a step list by r positions on the 16-grid (Euclid offset rotation carrier). */
function rotate(steps: number[], r: number): number[] {
  return steps.map((s) => (s + r + 16) % 16).sort((a, b) => a - b);
}

/** Rotation offset for a counter: +1 every 2 counter bars, 8 offsets, full cycle 16 bars. */
export function rotationFor(counter: number): number {
  return 2 * (Math.floor(Math.max(0, counter) / 2) % 8);
}

/**
 * PURE (plan 24 Goal (a)): the bar's final pattern spec after form selection + variation
 *  operators. Deterministic given (counter, state, flags, rng sequence position). The
 *  sequencer calls this once per booked bar; the tests pin every operator.
 *
 *  Operators (dossier §2 Variation grammar): Euclid snare offset rotation (all states,
 *  counter-driven — the frozen form counter, so engage interruptions pause the cycle);
 *  `?`-drops p=0.15 on ostinato and non-accent snare steps (engage/boss ONLY — march
 *  variation is structural, not random); ostinato pitch-parity alternation (engage/boss);
 *  boss escalation (+1 accent every 4th step + bright tom) when bossHp ≤ 50 %.
 */
export function barSpecFor(args: {
  patterns: MusicTheme['patterns'];
  form: FormSpec;
  counter: number;
  state: 'march' | 'engage' | 'boss';
  bossEntry: boolean;
  bossEscalated: boolean;
  rng: Rng;
}): BarSpec {
  const { patterns, form, counter, state, bossEntry, bossEscalated, rng } = args;
  const rotation = rotationFor(counter);
  const dropped: number[] = [];
  let parity = 0;

  let base: Pattern;
  let section: BarSpec['section'];
  let fill = false;
  let entryFill = false;
  let drop = false;

  if (bossEntry) {
    section = 'boss-entry';
    entryFill = true;
    base = form.entry;
  } else if (state === 'march' && counter > 0 && counter % 8 === 0) {
    section = 'fill';
    fill = true;
    base = form.fill;
  } else if (state === 'march' && counter === form.dropCounter) {
    section = 'drop';
    drop = true;
    base = { kick: [], snare: [], accent: [], tom: [], ostinato: [] };
  } else {
    section = sectionFor(counter);
    base = form.sections[section]?.[state] ?? patterns[state];
  }
  // Set figures (fill / drop / boss-entry) keep their authored grid — a roll must land on
  // its written accent. Section bars rotate (impl-gate r1 opus P1-1), and their GRACE hits
  // rotate with the strokes they decorate, so a flam never becomes an orphan ghost note.
  const rotationApplies = section === 'A' || section === "A'" || section === 'B';
  const r = rotationApplies ? rotation : 0;
  const rotateAt = (at: number): number => (at + r + 16) % 16;

  // Deep copy for operator application (never mutate the authored tables).
  const spec: Pattern = {
    kick: [...base.kick],
    snare: rotate(base.snare, r),
    accent: [...base.accent],
    tom: [...base.tom],
    ostinato: [...base.ostinato],
    grace: base.grace?.map((g) => ({ at: rotateAt(g.at), element: g.element })),
  };

  if (state !== 'march' && !fill && !entryFill && !drop) {
    // ?-drops: each ostinato step and each NON-accent snare removed with p = 0.15.
    spec.ostinato = spec.ostinato.filter((s) => {
      if (rng() < 0.15) {
        dropped.push(s);
        return false;
      }
      return true;
    });
    const accents = new Set(spec.accent);
    spec.snare = spec.snare.filter((s) => {
      if (!accents.has(s) && rng() < 0.15) {
        dropped.push(s);
        return false;
      }
      return true;
    });
    parity = counter % 2;
  }

  if (state === 'boss' && bossEscalated && !entryFill) {
    // Escalation: +1 accent density (every 4th step not already accented) + bright tom.
    const acc = new Set(spec.accent);
    for (let s = 0; s < 16; s += 4) if (!acc.has(s)) spec.accent.push(s);
    spec.accent.sort((a, b) => a - b);
  }

  const mixBySection: Record<string, BarSpec['mix']> = {
    A: { perc: 1, drone: 1, ostinato: 1 },
    "A'": { perc: 0.9, drone: 1, ostinato: 1 },
    B: { perc: 1, drone: 0.95, ostinato: 1 },
    fill: { perc: 1, drone: 1, ostinato: 0 },
    'boss-entry': { perc: 1, drone: 1, ostinato: 0 },
    drop: { perc: 0, drone: 1, ostinato: 0 },
  };

  const patternHash = JSON.stringify({ state, section, rotation, parity, dropped: [...dropped].sort((a, b) => a - b), bossEscalated: state === 'boss' && bossEscalated, bossEntry });
  return { section, pattern: spec, mix: mixBySection[section] ?? mixBySection.A, fill, entryFill, drop, brightTom: state === 'boss' && bossEscalated, patternHash };
}

/** One booked bar, as consumed by the offline harness metric (plan 24 Phase 0 item 3).
 *  Phase 0 emits the minimal record (counter/state/hash); Lane A populates section /
 *  fill / entryFill from the Form engine. */
export interface BarRecord {
  /** ctx-time the bar was booked AT (its musical start). */
  bookedAt: number;
  /** Form counter (plan 24 Phase 0 item 8 policy). */
  counter: number;
  state: 'march' | 'engage' | 'boss';
  section: string;
  /** Identity of the booked pattern state — Lane A makes this the real pattern hash. */
  patternHash: string;
  fill: boolean;
  entryFill: boolean;
}

/** PURE (plan 24 Phase 0 item 8): the counter ADVANCES on march/boss bars and is FROZEN
 *  during engage (the form resumes where it left — musical memory across fights). The
 *  in-flight march bar still counts when engage begins mid-bar: it was already booked. */
export function advanceCounter(counter: number, layer: 'march' | 'engage' | 'boss'): number {
  return layer === 'engage' ? counter : counter + 1;
}

/**
 * Per-level musical identity (plan 17 D3): tempo, drone root, ostinato pitches and the pattern
 * bank. The scheduler maths (booking window, bar bookkeeping, layer crossfade) is theme-agnostic.
 */
export interface MusicTheme {
  bpm: number;
  bossBpm: number;
  droneHz: number;
  ostinatoHz: number[];
  patterns: Record<'march' | 'engage' | 'boss', Pattern>;
}

/** L1 (Operation Highline): today's shipped theme VERBATIM — pinned field-for-field by music.test.ts. */
export const THEME_L1: MusicTheme = {
  bpm: 100,
  bossBpm: 112,
  droneHz: 55, // A1
  ostinatoHz: [220, 261.63, 329.63], // A-minor triad
  patterns: {
    march: {
      kick: [0, 8],
      snare: [4, 12],
      accent: [],
      tom: [],
      ostinato: [],
    },
    engage: {
      kick: [0, 6, 8, 14],
      snare: [2, 4, 6, 8, 10, 12, 14], // roll under the melody line
      accent: [4, 12],
      tom: [],
      ostinato: [0, 3, 8, 11],
    },
    boss: {
      kick: [0, 3, 8, 11],
      snare: [4, 12],
      accent: [0, 7, 12],
      tom: [6, 13, 14],
      ostinato: [0, 2, 8, 10, 12],
    },
  },
};

/** L2 (Operation Scrapline): darker, heavier sibling — E1 root (a fourth below A1), industrial bank.
 *  Plan 24 owner decision 1: march lifted 92 → 112 (authentic quick-march band 112–118, TC 1-19.10
 *  ¶2-17 — the level darkens AND quickens); boss keeps the +12 rule → 124. */
export const THEME_L2: MusicTheme = {
  bpm: 112,
  bossBpm: 124,
  droneHz: 41.2, // E1
  ostinatoHz: [164.81, 196.0, 246.94], // E-minor triad E3/G3/B3
  patterns: {
    march: {
      kick: [0, 10],
      snare: [6],
      accent: [],
      tom: [],
      ostinato: [],
    },
    engage: {
      kick: [0, 6, 10],
      snare: [4, 12],
      accent: [8],
      tom: [6, 13],
      ostinato: [0, 8],
    },
    boss: {
      kick: [0, 3, 8, 11],
      snare: [4, 12],
      accent: [0, 7, 12],
      tom: [6, 13, 14],
      ostinato: [0, 2, 4, 6, 8, 10, 12, 14], // driving straight eighths
    },
  },
};

/** L3 (Operation Duskline): the darkest sibling — D1 drone, D-minor. The 36.71 Hz
 *  fundamental is near-inaudible on phone speakers; the detuned-saw stack carries its harmonics
 *  (the plan-17 E1 precedent the owner A/B signed off — plan 19 D5 / opus r1 P2). March is a
 *  sparser dirge than L2's per-bar hit count, boss the heaviest bank yet (straight-eighth
 *  ostinato + tom weight). Plan 24 owner decision 1: march lifted 88 → 116, boss 100 → 128 —
 *  L3 is now the QUICKEST march (the finale drives; it stays the darkest by drone/pattern). */
export const THEME_L3: MusicTheme = {
  bpm: 116,
  bossBpm: 128,
  droneHz: 36.71, // D1
  ostinatoHz: [146.81, 174.61, 220], // D-minor triad D3/F3/A3
  patterns: {
    march: {
      kick: [0],
      snare: [8],
      accent: [],
      tom: [],
      ostinato: [],
    },
    engage: {
      kick: [0, 6, 10],
      snare: [4, 12],
      accent: [8],
      tom: [6, 13],
      ostinato: [0, 4, 8, 12], // quarter-note D-minor pulse under the fight
    },
    boss: {
      kick: [0, 3, 8, 11],
      snare: [4, 12],
      accent: [0, 7, 12],
      tom: [2, 6, 13, 14], // the heaviest tom weight of the three levels
      ostinato: [0, 2, 4, 6, 8, 10, 12, 14], // driving straight eighths
    },
  },
};

// Back-compat aliases (music.test.ts imports BPM as a fixture): L1 stays the default theme.
export const BPM = THEME_L1.bpm;
export const BOSS_BPM = THEME_L1.bossBpm;

// --- per-level song forms (plan 24 Goal (a)/(b): SEPARATE tables, THEME pins untouched) ---

/** L1 form — Field-Line vocabulary in the Highline march: A = the shipped march verbatim,
 *  A′ = flam pair before the accent, B = drag + ostinato pulse; the 8-bar fill is a 5-stroke
 *  snare/tom roll ending on the accent; entry = rising tom→snare 2-hit + build. */
export const FORM_L1: FormSpec = {
  sections: {
    A: { march: { kick: [0, 8], snare: [4, 12], accent: [], tom: [], ostinato: [] } },
    "A'": {
      march: {
        kick: [0, 8],
        snare: [4, 12],
        accent: [12],
        tom: [],
        ostinato: [],
        grace: [
          { at: 3.5, element: 'snare' }, // flam before the backbeat
          { at: 11.5, element: 'snare' }, // flam pair into the accent
        ],
      },
    },
    B: {
      march: {
        kick: [0, 8],
        snare: [4, 12],
        accent: [4],
        tom: [14],
        ostinato: [0, 8],
        grace: [
          { at: 3.25, element: 'snare' },
          { at: 3.75, element: 'snare' }, // drag: two graces before snare 4
        ],
      },
    },
  },
  fill: {
    kick: [0, 8],
    snare: [14],
    accent: [14],
    tom: [],
    ostinato: [],
    grace: [
      { at: 12.25, element: 'snare' },
      { at: 12.75, element: 'tom' },
      { at: 13.25, element: 'snare' },
      { at: 13.75, element: 'tom' }, // 5-stroke roll (alternating doubles, 32nds)
    ],
  },
  entry: { kick: [0], snare: [15], accent: [15], tom: [12, 14], ostinato: [] },
  dropCounter: 11,
};

/** L2 form — the industrial bank darkens: A = shipped march, A′ adds the tom heartbeat,
 *  B rides the straight-eighth ostinato; heavier fill (7-stroke), same entry grammar. */
export const FORM_L2: FormSpec = {
  sections: {
    A: { march: { kick: [0, 10], snare: [6], accent: [], tom: [], ostinato: [] } },
    "A'": {
      march: {
        kick: [0, 10],
        snare: [6],
        accent: [6],
        tom: [2],
        ostinato: [],
        grace: [{ at: 5.5, element: 'snare' }],
      },
    },
    B: {
      march: {
        kick: [0, 10],
        snare: [6],
        accent: [6],
        tom: [2, 14],
        ostinato: [0, 8],
        grace: [
          { at: 5.25, element: 'snare' },
          { at: 5.75, element: 'snare' },
        ],
      },
    },
  },
  fill: {
    kick: [0],
    snare: [15],
    accent: [15],
    tom: [11],
    ostinato: [],
    grace: [
      { at: 11.25, element: 'snare' },
      { at: 11.75, element: 'snare' },
      { at: 12.25, element: 'tom' },
      { at: 12.75, element: 'snare' },
      { at: 13.25, element: 'tom' },
      { at: 13.75, element: 'snare' }, // 7-stroke roll
    ],
  },
  entry: { kick: [0], snare: [15], accent: [15], tom: [12, 14], ostinato: [] },
  dropCounter: 11,
};

/** L3 form — the dusk dirge drives hardest: A = the one-kick dirge verbatim, A′ the drag
 *  variant, B brings the ostinato pulse forward (the finale quickens); fill mirrors L1. */
export const FORM_L3: FormSpec = {
  sections: {
    A: { march: { kick: [0], snare: [8], accent: [], tom: [], ostinato: [] } },
    "A'": {
      march: {
        kick: [0],
        snare: [8],
        accent: [8],
        tom: [],
        ostinato: [],
        grace: [
          { at: 7.25, element: 'snare' },
          { at: 7.75, element: 'snare' }, // drag into the downbeat snare
        ],
      },
    },
    B: {
      march: {
        kick: [0],
        snare: [8],
        accent: [8],
        tom: [4, 12],
        ostinato: [0, 4, 8, 12],
        grace: [{ at: 3.5, element: 'tom' }],
      },
    },
  },
  fill: {
    kick: [0, 8],
    snare: [14],
    accent: [14],
    tom: [],
    ostinato: [],
    grace: [
      { at: 12.25, element: 'snare' },
      { at: 12.75, element: 'tom' },
      { at: 13.25, element: 'snare' },
      { at: 13.75, element: 'tom' },
    ],
  },
  entry: { kick: [0], snare: [15], accent: [15], tom: [12, 14], ostinato: [] },
  dropCounter: 11,
};

/**
 * Plan 27 (boss reveal, owner decision 3): the boss layer enters at +6 dB for the ENTRY bar and
 * settles to +3 dB for the rest of the fight. The entry peak lives ONLY in the scheduled
 * automation; the `boost` FIELD that `switchLayer` crossfades toward flips straight to the settle
 * value AT the slam, so any later re-entry targets 1.4 and can never override the settle ramp
 * (plan 27 r3 opus N-4).
 */
export const BOSS_ENTRY_BOOST = 2.0;
export const BOSS_SETTLE_BOOST = 1.4;
/** The entry bar's anchored attack (50 ms) and the settle ramp (0.25 s) — plan 27 Phase 2. */
export const BOSS_ENTRY_ATTACK_S = 0.05;
export const BOSS_SETTLE_RAMP_S = 0.25;

/**
 * One scheduled AudioParam write (plan 27 Phase 2). The reveal automation is built as DATA by the
 * pure planners below, so vitest pins the scheduled-arg sequences in node WITHOUT Web Audio (the
 * repo has no stub-ctx precedent; the only `AudioContext` a test ever touches is a `vi.fn()`
 * asserting it is NOT constructed). `applyGainWrites` is the single Web Audio caller.
 */
export interface GainWrite {
  kind: 'setValueAtTime' | 'linearRampToValueAtTime' | 'setTargetAtTime';
  value: number;
  at: number;
  /** `setTargetAtTime` only: the exponential approach constant. */
  timeConstant?: number;
}

/**
 * Apply a plan to a real param. `cancelAt` (when given) cancels every automation scheduled AT or
 * AFTER that time first — the click-free "kill the lookahead-booked crossfade" step. Never
 * `cancelAndHoldAtTime` (never shipped in Firefox — plan 24 impl-gate r1 opus P0-1).
 */
export function applyGainWrites(param: AudioParam, writes: readonly GainWrite[], cancelAt?: number): void {
  if (cancelAt !== undefined) param.cancelScheduledValues(cancelAt);
  for (const w of writes) {
    if (w.kind === 'setValueAtTime') param.setValueAtTime(w.value, w.at);
    else if (w.kind === 'linearRampToValueAtTime') param.linearRampToValueAtTime(w.value, w.at);
    else param.setTargetAtTime(w.value, w.at, w.timeConstant ?? 0.015);
  }
}

/** The sequencer's booking layers (plan 27: the slam forces one of these). The `Intensity` union of
 *  cues.ts additionally carries the terminal won/lost, which never books a bar. */
export type Layer = 'march' | 'engage' | 'boss';

/**
 * PURE (plan 27 Phase 2): the boss-entry figure latches on the TRANSITION into the boss layer —
 * `null`/march/engage ⇒ this bar is the entry bar, `boss` ⇒ a normal boss bar. ONE rule shared by
 * the bar-line path (`bookUpTo`) and the slam, which is what makes the entry figure fire exactly
 * once across the slam plus every subsequent tick.
 */
export function bossEntryFor(intensity: Layer, lastBookedState: Layer | null): boolean {
  return intensity === 'boss' && lastBookedState !== 'boss';
}

/** The pure slam plan — the automation + the state the sequencer installs (plan 27 Phase 2). */
export interface SlamPlan {
  /** The arrival instant; every write anchors here (ctx-time). */
  at: number;
  /** Re-anchored bar grid: the entry bar starts ON the arrival frame (unquantized). */
  barStart: number;
  /** One entry bar at the BOSS tempo — never a hardcoded bar. */
  barDuration: number;
  /** `at + barDuration`: the entry bar's end = the settle's anchor point. */
  entryEnd: number;
  /** The entry peak. Scheduled automation ONLY, never the FIELD. */
  entryBoost: number;
  /** The FIELD `switchLayer` crossfades toward from the slam onward (the settle value). */
  boostField: number;
  /** Per-group writes, all cancelled at `at` first. March/engage → 0 is exactly the end state
   *  `switchLayer('boss')` would reach (plan 27 r2 opus N-4). */
  groups: { march: GainWrite[]; engage: GainWrite[]; boss: GainWrite[] };
  /** The latch value slamBoss installs AFTER booking, so the next tick books a NORMAL boss bar. */
  lastBookedStateAfterBooking: 'boss';
}

/**
 * PURE (plan 27 Phase 2 item 1): the whole boss-reveal slam as data. The boss group goes 0 →
 * `entryBoost` over a 50 ms anchored attack, HOLDS through the entry bar (the setValueAtTime at
 * `entryEnd` is the anchor that keeps the settle a 0.25 s ramp instead of a 1.8 s one) and ramps to
 * `settleBoost` at `entryEnd + 0.25`.
 */
export function slamBossPlan(args: { at: number; bossBpm: number; entryBoost?: number; settleBoost?: number }): SlamPlan {
  const at = args.at;
  const entryBoost = args.entryBoost ?? BOSS_ENTRY_BOOST;
  const settleBoost = args.settleBoost ?? BOSS_SETTLE_BOOST;
  const dur = barDuration(args.bossBpm);
  const entryEnd = at + dur;
  return {
    at,
    barStart: at,
    barDuration: dur,
    entryEnd,
    entryBoost,
    boostField: settleBoost,
    groups: {
      march: [{ kind: 'setValueAtTime', value: 0, at }],
      engage: [{ kind: 'setValueAtTime', value: 0, at }],
      boss: [
        { kind: 'setValueAtTime', value: 0, at },
        { kind: 'linearRampToValueAtTime', value: entryBoost, at: at + BOSS_ENTRY_ATTACK_S },
        { kind: 'setValueAtTime', value: entryBoost, at: entryEnd },
        { kind: 'linearRampToValueAtTime', value: settleBoost, at: entryEnd + BOSS_SETTLE_RAMP_S },
      ],
    },
    lastBookedStateAfterBooking: 'boss',
  };
}

/**
 * PURE (plan 27 Phase 2): what happens AT a bar line BEFORE a bar is booked — the pending layer
 * switch (bar-quantized crossfade), the `reset()` march re-open, the counter advance and the
 * boss-entry latch. `bookUpTo` APPLIES this; it owns no decision of its own, so the node tests pin
 * the real booking semantics (including "the slam's entry figure fires exactly once, because the
 * next tick's `lastBookedState` is already 'boss'") without Web Audio.
 */
export function barLinePlan(args: {
  intensity: Layer;
  pending: Layer | null;
  forceMarch: boolean;
  counter: number;
  lastBookedState: Layer | null;
}): { action: 'forceMarch' | 'switch' | 'none'; intensity: Layer; counter: number; bossEntry: boolean } {
  const switched = !args.forceMarch && args.pending !== null && args.pending !== args.intensity;
  const intensity = switched ? (args.pending as Layer) : args.intensity;
  return {
    action: args.forceMarch ? 'forceMarch' : switched ? 'switch' : 'none',
    intensity,
    counter: advanceCounter(args.counter, intensity),
    bossEntry: bossEntryFor(intensity, args.lastBookedState),
  };
}

/**
 * PURE (plan 27 Phase 2 item 2): the gain a layer crossfades IN to. Only the boss layer carries the
 * reveal boost (1.0 default ⇒ the skip path and every pre-reveal bar are verbatim; 1.4 after a
 * slam), everything else lands on unity.
 */
export function crossfadeTargetFor(layer: Layer, boost: number): number {
  return layer === 'boss' ? boost : 1;
}

/**
 * PURE (plan 27 Phase 2): the sequencer state `slamBoss` installs BEFORE booking, i.e. the grid
 * re-anchor + the forced layer. `lastBookedState: null` is the entry latch — it is what makes the
 * booking path fire the entry figure; `boost` is the FIELD (settle), not the entry peak.
 */
export function slamSequencerState(plan: SlamPlan): {
  barStart: number;
  intensity: 'boss';
  pending: null;
  forceMarch: false;
  lastBookedState: null;
  boost: number;
} {
  return {
    barStart: plan.barStart,
    intensity: 'boss',
    pending: null,
    forceMarch: false,
    lastBookedState: null,
    boost: plan.boostField,
  };
}

/** Identity-map a theme to its form (the director hands over only the theme; plan 24 keeps
 *  audio.ts form-agnostic). Unknown themes fall back to L1's form. */
export function formFor(theme: MusicTheme): FormSpec {
  if (theme === THEME_L2) return FORM_L2;
  if (theme === THEME_L3) return FORM_L3;
  return FORM_L1;
}

const FOURTH = Math.pow(2, 5 / 12);

/**
 * The sequencer. Owns nothing but scheduling state; the AudioContext and bus arrive per call from
 * audio.ts (it must survive the lazy unlock, so it cannot capture a context at construction).
 */
export class MusicSequencer {
  /** Variation stream (plan 24 Phase 0 item 1): pinned in tests/renders, runSeed() in
   *  play. Lane A's form operators draw from it; Phase 0 wires it, nothing draws yet. */
  readonly rng: Rng;
  /** Form data + boss-escalation input (plan 24 Goal (a)). */
  private readonly form: FormSpec;
  private lastBookedState: 'march' | 'engage' | 'boss' | null = null;
  private bossHpFrac = 1;
  constructor(
    private readonly theme: MusicTheme = THEME_L1,
    rng: Rng = makeRng(runSeed()),
  ) {
    this.rng = rng;
    this.form = formFor(theme);
  }

  private readonly voices = new VoicePool();
  private intensity: 'march' | 'engage' | 'boss' = 'march';
  private pending: 'march' | 'engage' | 'boss' | null = null;
  /**
   * Plan 27 (boss reveal): the boss-group gain FIELD `switchLayer` crossfades toward. Default 1.0
   * keeps the skip path (and every pre-reveal bar) verbatim; `slamBoss` flips it to
   * {@link BOSS_SETTLE_BOOST} at the slam, so post-slam re-entries stay consistent and can never
   * override the scheduled settle ramp. The entry bar's {@link BOSS_ENTRY_BOOST} lives in the
   * SCHEDULED automation only (plan 27 r3 opus N-4). `reset()` restores 1.0.
   */
  private boost = 1.0;
  private paused = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  /** ctx-time of the grid origin and of the bar currently being booked. */
  private originTime = 0;
  private barStart = 0;
  private stopped = false; // won/lost: booked everything, only reset() resumes
  private forceMarch = false; // reset(): re-open the march group even if it was already current
  /** Form counter + bar-record sink (plan 24 Phase 0 items 3/8). */
  private barCounter = 0;
  private bookedBars = 0;
  onBar: ((bar: BarRecord) => void) | null = null;
  /** Plan 27 Phase 2: the test-only entry-boost override (see setEntryBoostOverride). */
  private entryBoostOverride: number | undefined = undefined;

  /** Plan 24 Goal (a): audio.ts forwards cues' bossTension every update; ≤ 0.5 escalates
   *  subsequent boss bars (immediate, not bar-quantized — applies to bars booked after the
   *  flag flips). */
  setBossHpFrac(frac: number): void {
    this.bossHpFrac = Number.isFinite(frac) ? Math.max(0, Math.min(1, frac)) : 1;
  }

  /** Start the 25 ms scheduler loop on an unlocked context; `dest` is the music bus. Idempotent. */
  start(ctx: BaseAudioContext, dest: AudioNode): void {
    if (this.timer !== null) return;
    this.originTime = ctx.currentTime;
    this.barStart = ctx.currentTime;
    const g = this.group(ctx, dest, this.intensity);
    g.gain.cancelScheduledValues(ctx.currentTime);
    g.gain.setValueAtTime(0, ctx.currentTime);
    // Plan 27: the fade-in targets the layer's OWN crossfade gain, so a re-start (unmute after an
    // iOS interruption) while the boosted boss layer is current does not quietly drop it to unity.
    g.gain.linearRampToValueAtTime(crossfadeTargetFor(this.intensity, this.boost), ctx.currentTime + 0.5); // fade in, no pop at unlock
    this.timer = setInterval(() => this.tick(ctx, dest), TICK_MS);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.voices.clear();
  }

  /** Pause stops BOOKING new bars; the bar already booked (≤ one bar, ~2.1 s) still plays out —
   *  only the debug pause hits this, and mute is instant via the master gain. */
  setPaused(p: boolean): void {
    this.paused = p;
  }

  /** True once start() has armed the scheduler loop (audio.ts restarts after an unmute). */
  isRunning(): boolean {
    return this.timer !== null;
  }

  /**
   * Plan 27 Phase 2 read seam (state, not automation): the boss-group FIELD (1.0 default, 1.4
   * post-slam). The entry peak is NOT here — it is in the scheduled writes of the slam plan.
   */
  boostNow(): number {
    return this.boost;
  }

  /** Plan 27 Phase 2 read seam: the entry latch ('boss' ⇒ the next booked bar is a NORMAL boss bar). */
  lastBookedStateNow(): 'march' | 'engage' | 'boss' | null {
    return this.lastBookedState;
  }

  /** Plan 27 Phase 2 read seam: the ctx-time of the bar currently being booked (the grid head). */
  barStartNow(): number {
    return this.barStart;
  }

  /** Current loop layer (pending switches apply at the next bar line). */
  intensityNow(): 'march' | 'engage' | 'boss' {
    return this.intensity;
  }

  /** Restart: back to bar 1 of the grid, un-sting; the layer is re-selected by the next update().
   *  `reset()` restores `originTime` (the stale-`barStart` re-anchor happens in tick()); the
   *  form counter RESETS with it (plan 24 Phase 0 item 8: any restart/next-level/retry). */
  reset(): void {
    this.stopped = false;
    this.pending = null;
    this.forceMarch = true;
    this.intensity = 'march';
    this.barStart = this.originTime; // origin re-anchored at start(); setPaused persists across restarts
    this.barCounter = 0;
    this.bookedBars = 0;
    this.lastBookedState = null; // the next boss is an ENTRY again (entry fill re-fires)
    this.boost = 1.0; // plan 27: the reveal boost is a one-reveal thing, not a per-save setting
  }

  /**
   * Plan 27 Phase 2 item 1: the UNQUANTIZED boss entry on the arrival frame (the step that flips
   * `phase: 'run' → 'boss'`). Everything anchors on `at`:
   *
   * 1. `voices.clear()` — kills scheduled-but-unstarted sources too (the ducked bar's notes must
   *    never sound after the slam),
   * 2. cancel + anchor ALL THREE group gains at `at` — any lookahead-booked crossfade dies there;
   *    march/engage anchor to 0 (= the `switchLayer('boss')` end state, r2 opus N-4),
   * 3. the bar grid re-anchors to `at` and the layer is FORCED to boss (pending/forceMarch cleared),
   * 4. the entry bar is booked through the existing booking path at `at` (bossEntry figure fires,
   *    boss group 0 → {@link BOSS_ENTRY_BOOST} over a 50 ms anchored attack), then
   *    `lastBookedState = 'boss'` so the next tick books a NORMAL boss bar — the entry figure can
   *    never fire twice,
   * 5. {@link BOSS_SETTLE_BOOST} is scheduled from `at + barDuration(theme.bossBpm)` over a 0.25 s
   *    anchored ramp, and the boost FIELD flips to it immediately.
   *
   * The A/B seam is ONE path (plan 27 fix round, copilot P1): {@link
   * MusicSequencer.setEntryBoostOverride}, forwarded from `CreateAudioOpts.entryBoost` (the only
   * thing the harness sets); `slamBossPlan`'s optional arg is that override's internal carrier.
   */
  slamBoss(ctx: BaseAudioContext, dest: AudioNode, at: number): void {
    const plan = slamBossPlan({
      at,
      bossBpm: this.theme.bossBpm,
      entryBoost: this.entryBoostOverride,
    });
    this.voices.clear();
    applyGainWrites(this.group(ctx, dest, 'march').gain, plan.groups.march, at);
    applyGainWrites(this.group(ctx, dest, 'engage').gain, plan.groups.engage, at);
    applyGainWrites(this.group(ctx, dest, 'boss').gain, plan.groups.boss, at);
    this.applySlamState(plan);
    // The existing booking path books the entry bar (BarRecord semantics, counter policy, rng
    // draws all unchanged); the horizon is exactly one entry bar, so no second bar sneaks in.
    this.bookUpTo(ctx, dest, plan.barStart + plan.barDuration);
    this.lastBookedState = plan.lastBookedStateAfterBooking;
  }

  /**
   * Plan 27 Phase 2: the STATE half of {@link MusicSequencer.slamBoss} (the automation + the
   * booking need a context, the state transition does not). Public so the node-environment tests
   * pin the transition table — barStart re-anchor, forced layer, entry latch, boost FIELD —
   * without Web Audio. slamBoss is its only production caller.
   */
  applySlamState(plan: SlamPlan): void {
    const next = slamSequencerState(plan);
    this.barStart = next.barStart;
    this.intensity = next.intensity;
    this.pending = next.pending;
    this.forceMarch = next.forceMarch;
    this.lastBookedState = next.lastBookedState;
    this.boost = next.boost;
  }

  /**
   * Plan 27 Phase 2 item 2 — TEST-ONLY injection seam for the Phase 3 A/B boost gate
   * (`tools/render-audio.mjs` renders the SAME music-solo reveal arm with 1.0 and 2.0 and asserts
   * the ≥ 2 dB entry-bar difference, so the ONLY thing that differs is the boost). It replaces the
   * {@link BOSS_ENTRY_BOOST} default of `slamBoss` (the FIELD/settle math is untouched). Gameplay
   * never calls it; `undefined` restores the shipped default. Plan 27 fix round (copilot P1): this is
   * the ONE injection path — `slamBoss` takes no boost argument of its own.
   */
  setEntryBoostOverride(boost: number | undefined): void {
    this.entryBoostOverride = boost;
  }

  /**
   * Layer request from cues.intensity(). March/engage/boss take effect on the next bar line with
   * a 1-bar crossfade; won/lost are terminal (stinger + silence until reset()).
   */
  setIntensity(next: Intensity): void {
    if (next === this.intensity || this.pending === next) return;
    if (next === 'won' || next === 'lost') {
      this.stopped = true;
      this.pending = null;
      return; // audio.ts plays the stinger cue; the loop just stops booking
    }
    if (this.stopped) return;
    this.pending = next;
  }

  /** Book any bar whose start enters the ≤ LOOKAHEAD_S window. */
  private tick(ctx: BaseAudioContext, dest: AudioNode): void {
    if (this.paused || this.stopped) return;
    // A pause or reset can leave barStart in the past; re-anchor to now so the catch-up loop can
    // never book hundreds of back-dated bars (notes in the past would all fire at once).
    if (this.barStart < ctx.currentTime) this.barStart = ctx.currentTime;
    this.bookUpTo(ctx, dest, ctx.currentTime + LOOKAHEAD_S);
  }

  /** Phase 0 item 4: the booking loop as a public seam — tick() drives it on the production
   *  clock; an offline driver (the render harness) steps it at computed times. Emits one
   *  BarRecord per booked bar with the counter policy applied. */
  bookUpTo(ctx: BaseAudioContext, dest: AudioNode, horizon: number): void {
    // One bar per call is plenty (bar ≥ 2.1 s ≫ 100 ms horizon); the loop guards clock skew.
    while (this.barStart < horizon) {
      // The bar-line DECISION is the pure `barLinePlan` (plan 27); this loop only applies it.
      const line = barLinePlan({
        intensity: this.intensity,
        pending: this.pending,
        forceMarch: this.forceMarch,
        counter: this.barCounter,
        lastBookedState: this.lastBookedState,
      });
      if (line.action === 'forceMarch') {
        const g = this.group(ctx, dest, 'march');
        g.gain.cancelScheduledValues(this.barStart);
        g.gain.setValueAtTime(g.gain.value, this.barStart);
        g.gain.linearRampToValueAtTime(1, this.barStart + 0.1);
        for (const [layer, other] of this.groups) {
          if (layer !== 'march') {
            other.gain.cancelScheduledValues(this.barStart);
            other.gain.setValueAtTime(other.gain.value, this.barStart);
            other.gain.linearRampToValueAtTime(0, this.barStart + 0.1);
          }
        }
      } else if (line.action === 'switch') {
        this.switchLayer(ctx, dest, line.intensity, this.barStart);
      }
      this.forceMarch = false;
      this.pending = null;
      this.intensity = line.intensity;
      this.barCounter = line.counter;
      const spec = barSpecFor({
        patterns: this.theme.patterns,
        form: this.form,
        counter: this.barCounter,
        state: this.intensity,
        bossEntry: line.bossEntry,
        bossEscalated: this.bossHpFrac <= 0.5,
        rng: this.rng,
      });
      this.bookBar(ctx, dest, this.barStart, this.intensity, spec);
      this.bookedBars++;
      this.lastBookedState = this.intensity;
      this.onBar?.({
        bookedAt: this.barStart,
        counter: this.barCounter,
        state: this.intensity,
        section: spec.section,
        patternHash: spec.patternHash,
        fill: spec.fill,
        entryFill: spec.entryFill,
      });
      this.barStart += barDuration(this.bpmFor(this.intensity));
    }
  }

  private bpmFor(layer: 'march' | 'engage' | 'boss'): number {
    return layer === 'boss' ? this.theme.bossBpm : this.theme.bpm;
  }

  /**
   * Per-layer gain groups on the music bus. A switch retimes `intensity` at the bar line and
   * crossfades old → new over exactly one bar, so layers change musically, never mid-phrase.
   */
  private readonly groups = new Map<Intensity, GainNode>();

  private group(ctx: BaseAudioContext, dest: AudioNode, layer: Intensity): GainNode {
    let g = this.groups.get(layer);
    if (!g) {
      g = ctx.createGain();
      g.gain.value = 0;
      g.connect(dest);
      this.groups.set(layer, g);
    }
    return g;
  }

  private switchLayer(ctx: BaseAudioContext, dest: AudioNode, next: 'march' | 'engage' | 'boss', at: number): void {
    const prev = this.intensity;
    this.intensity = next;
    const dur = barDuration(this.bpmFor(next));
    const inGain = this.group(ctx, dest, next);
    const outGain = this.group(ctx, dest, prev);
    // Plan 27 Phase 2 item 2: the boss crossfade targets the current boost FIELD (1.0 pre-reveal,
    // {@link BOSS_SETTLE_BOOST} post-slam), so a post-slam re-entry stays consistent with the
    // settle the slam already scheduled. The entry peak is never a crossfade target.
    const inTarget = crossfadeTargetFor(next, this.boost);
    for (const [g, target] of [[inGain, inTarget], [outGain, 0]] as const) {
      g.gain.cancelScheduledValues(at);
      g.gain.setValueAtTime(g.gain.value, at);
      g.gain.linearRampToValueAtTime(target, at + dur);
    }
  }

  private bookBar(ctx: BaseAudioContext, dest: AudioNode, barAt: number, layer: 'march' | 'engage' | 'boss', spec: BarSpec): void {
    const bpm = this.bpmFor(layer);
    const step = stepDuration(bpm);
    const barDur = barDuration(bpm);
    const pattern = spec.pattern;
    const bus = this.group(ctx, dest, layer);
    const motif = combatMotifForBar(this.theme, layer, this.bookedBars, spec);
    for (const k of stepsInWindow(barAt, barAt + barDur, barAt, step)) {
      const at = barAt + k * step;
      if (pattern.kick.includes(k)) this.drum(ctx, bus, at, 120, 40, 0.28 * spec.mix.perc, 0.18);
      if (pattern.snare.includes(k)) this.snare(ctx, bus, at, (pattern.accent.includes(k) ? 0.16 : 0.09) * spec.mix.perc);
      if (pattern.tom.includes(k)) this.drum(ctx, bus, at, spec.brightTom ? 240 : 180, 70, 0.22 * spec.mix.perc, 0.26);
      if (!motif.length && pattern.ostinato.includes(k))
        this.pluck(
          ctx,
          bus,
          at,
          this.theme.ostinatoHz[k % this.theme.ostinatoHz.length] * (layer === 'boss' ? FOURTH : 1),
          spec.mix.ostinato,
        );
      for (const note of motif) {
        if (Math.floor(note.offset / step) === k)
          tone(ctx, bus, this.voices, barAt + note.offset, { freq: note.frequency, peak: note.gain * spec.mix.ostinato, attack: 0.004, decay: note.duration, type: 'triangle' });
      }
    }
    // Field-Line grace hits (plan 24): 32nd-note flams/drags/roll doubles at half-step
    // offsets, lower velocity, tom grace brighter when escalated.
    if (pattern.grace) {
      for (const g of pattern.grace) {
        const at = barAt + g.at * step;
        if (g.element === 'snare') this.snare(ctx, bus, at, 0.045 * spec.mix.perc);
        else this.drum(ctx, bus, at, spec.brightTom ? 220 : 165, 70, 0.06 * spec.mix.perc, 0.12);
      }
    }
    // Drone: one bar-long bowed swell per bar keeps it glitch-free across tempo changes.
    this.drone(ctx, bus, barAt, barDur, layer, spec.mix.drone);
  }

  // --- percussion voices (pitched-sine drums + a noise/tone snare; the drone breathes per bar) ---

  /** Kick and floor tom are the same recipe — pitched sine glissando with a fast attack. */
  private drum(ctx: BaseAudioContext, dest: AudioNode, at: number, f0: number, f1: number, peak: number, decay: number): void {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f0, at);
    osc.frequency.exponentialRampToValueAtTime(f1, at + decay * 0.7);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(peak, at + 0.007);
    g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    osc.connect(g);
    g.connect(dest);
    osc.start(at);
    osc.stop(at + decay + 0.02);
    this.voices.register(osc, [osc, g]);
  }

  private snare(ctx: BaseAudioContext, dest: AudioNode, at: number, peak: number): void {
    const src = ctx.createBufferSource();
    src.buffer = musicNoise(ctx);
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.value = 1500;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(peak, at + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.11);
    src.connect(f);
    f.connect(g);
    g.connect(dest);
    src.start(at, (at * 7.13) % 1.5);
    src.stop(at + 0.13);
    const stick = ctx.createOscillator();
    stick.type = 'triangle';
    stick.frequency.value = 190; // stick tone
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(peak * 0.5, at);
    tg.gain.exponentialRampToValueAtTime(0.0001, at + 0.06);
    stick.connect(tg);
    tg.connect(dest);
    stick.start(at);
    stick.stop(at + 0.08);
    this.voices.register(src, [src, f, g]);
    this.voices.register(stick, [stick, tg]);
  }

  private pluck(ctx: BaseAudioContext, dest: AudioNode, at: number, hz: number, gain = 1): void {
    // Sparse triangle pluck on the drone's pentatonic — no panner, the music bus is already stereo.
    tone(ctx, dest, this.voices, at, { freq: hz, peak: 0.08 * gain, attack: 0.004, decay: 0.28, type: 'triangle' });
  }

  private drone(ctx: BaseAudioContext, dest: AudioNode, at: number, dur: number, layer: 'march' | 'engage' | 'boss', mix = 1): void {
    const root = this.theme.droneHz * (layer === 'boss' ? FOURTH : 1);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = layer === 'march' ? 320 : 520;
    lp.Q.value = 0.7;
    lp.connect(dest);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.linearRampToValueAtTime(0.07 * mix, at + dur * 0.4); // swell
    g.gain.setValueAtTime(0.07 * mix, at + dur * 0.75);
    g.gain.linearRampToValueAtTime(0.0001, at + dur); // breath out before the next bar's re-attack
    g.connect(lp);
    for (const detune of [-7, 0, 7]) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = root;
      osc.detune.value = detune;
      osc.connect(g);
      osc.start(at);
      osc.stop(at + dur + 0.02);
      if (detune === 7) this.voices.register(osc, [osc, g, lp]);
    }
  }
}

// A separate 1 s noise buffer keeps snare phase independent of the SFX bursts (synth.ts has its own).
let musicBuf: AudioBuffer | null = null;
let musicBufRate = 0;
function musicNoise(ctx: BaseAudioContext): AudioBuffer {
  if (!musicBuf || musicBufRate !== ctx.sampleRate) {
    musicBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = musicBuf.getChannelData(0);
    let s = 0x9e3779b9;
    for (let i = 0; i < d.length; i++) {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      d[i] = (s / 0x7fffffff) * 2 - 1;
    }
    musicBufRate = ctx.sampleRate;
  }
  return musicBuf;
}
